import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  RuleDefinition,
  CreateRuleDTO,
  UpdateRuleDTO,
  RuleFilters,
  RuleAuditLog,
  RuleCategory,
  CreateRuleCategoryDTO,
  RuleTemplate,
  CreateRuleFromTemplateDTO,
  ParseFormulaRequest,
  ParseFormulaResponse,
  TestRuleRequest,
  TestRuleResponse
} from '@core/models/api/api-models';

/**
 * Rule service for managing validation rules
 */
@Injectable({
  providedIn: 'root'
})
export class RuleService extends ApiService {
  private readonly endpoint = 'rules';

  private _categories$: Observable<RuleCategory[]> | null = null;
  /** One cached request per ruleType filter; '' holds the unfiltered list. */
  private readonly _templatesByType = new Map<string, Observable<RuleTemplate[]>>();

  /**
   * Get all rules with optional filters
   * GET /api/rules
   */
  getRules(filters?: RuleFilters): Observable<RuleDefinition[]> {
    const params = filters ? this.buildParams(filters) : new HttpParams();
    return this.get<RuleDefinition[]>(this.endpoint, params);
  }

  /**
   * Get a specific rule by ID
   * GET /api/rules/{ruleId}
   */
  getRuleById(id: string): Observable<RuleDefinition> {
    return this.get<RuleDefinition>(`${this.endpoint}/${id}`);
  }

  /**
   * Create a new validation rule
   * POST /api/rules
   */
  createRule(rule: CreateRuleDTO): Observable<RuleDefinition> {
    return this.post<RuleDefinition>(this.endpoint, rule);
  }

  /**
   * Update an existing rule
   * PUT /api/rules/{ruleId}
   */
  updateRule(id: string, rule: UpdateRuleDTO): Observable<RuleDefinition> {
    return this.put<RuleDefinition>(`${this.endpoint}/${id}`, rule);
  }

  /**
   * Soft delete a rule (sets IsActive = false)
   * DELETE /api/rules/{ruleId}
   */
  deleteRule(id: string): Observable<void> {
    return this.delete<void>(`${this.endpoint}/${id}`);
  }

  /**
   * Activate a rule
   * POST /api/rules/{ruleId}/activate
   */
  activateRule(id: string): Observable<RuleDefinition> {
    return this.post<RuleDefinition>(`${this.endpoint}/${id}/activate`, {});
  }

  /**
   * Deactivate a rule
   * POST /api/rules/{ruleId}/deactivate
   */
  deactivateRule(id: string): Observable<RuleDefinition> {
    return this.post<RuleDefinition>(`${this.endpoint}/${id}/deactivate`, {});
  }

  /**
   * Get audit history for a specific rule
   * GET /api/rules/{ruleId}/audit
   */
  getRuleAuditHistory(id: string): Observable<RuleAuditLog[]> {
    return this.get<RuleAuditLog[]>(`${this.endpoint}/${id}/audit`);
  }

  /**
   * Reset system default rule to original configuration
   * POST /api/rules/{ruleId}/reset
   */
  resetToDefault(id: string): Observable<RuleDefinition> {
    return this.post<RuleDefinition>(`${this.endpoint}/${id}/reset`, {});
  }

  // ============================================================================
  // Category Endpoints
  // ============================================================================

  /**
   * Get all rule categories
   * GET /api/rules/categories
   */
  getCategories(): Observable<RuleCategory[]> {
    if (!this._categories$) {
      this._categories$ = this.get<RuleCategory[]>(`${this.endpoint}/categories`).pipe(shareReplay(1));
    }
    return this._categories$;
  }

  /**
   * Create a new category (admin only)
   * POST /api/rules/categories
   */
  createCategory(category: CreateRuleCategoryDTO): Observable<RuleCategory> {
    return this.post<RuleCategory>(`${this.endpoint}/categories`, category);
  }

  // ============================================================================
  // Template Endpoints
  // ============================================================================

  /**
   * Get all rule templates, optionally filtered by type
   * GET /api/rules/templates?ruleType={type}
   */
  getTemplates(ruleType?: string): Observable<RuleTemplate[]> {
    const key = ruleType ?? '';
    let templates$ = this._templatesByType.get(key);
    if (!templates$) {
      const params = ruleType ? this.buildParams({ ruleType }) : undefined;
      templates$ = this.get<RuleTemplate[]>(`${this.endpoint}/templates`, params).pipe(shareReplay(1));
      this._templatesByType.set(key, templates$);
    }
    return templates$;
  }

  /**
   * Get a specific template by ID
   * GET /api/rules/templates/{templateId}
   */
  getTemplateById(id: string): Observable<RuleTemplate> {
    return this.get<RuleTemplate>(`${this.endpoint}/templates/${id}`);
  }

  /**
   * Create a rule from a template
   * POST /api/rules/from-template/{templateId}
   */
  createRuleFromTemplate(dto: CreateRuleFromTemplateDTO): Observable<RuleDefinition> {
    return this.post<RuleDefinition>(
      `${this.endpoint}/from-template/${dto.templateId}`,
      dto
    );
  }

  // ============================================================================
  // Formula Parsing
  // ============================================================================

  /**
   * Parse Excel formula into structured sources
   * POST /api/rules/parse-formula
   */
  parseFormula(request: ParseFormulaRequest): Observable<ParseFormulaResponse> {
    return this.post<ParseFormulaResponse>(`${this.endpoint}/parse-formula`, request);
  }

  // ============================================================================
  // Rule Testing
  // ============================================================================

  /**
   * Test a rule configuration against uploaded sample file
   * POST /api/rules/test
   */
  testRule(request: TestRuleRequest, file: File): Observable<TestRuleResponse> {
    const formData = new FormData();
    formData.append('excelFile', file);
    formData.append('ruleType', request.ruleType);
    formData.append('configuration', JSON.stringify(request.configuration));
    formData.append('targetSheets', JSON.stringify(request.targetSheets));

    return this.post<TestRuleResponse>(`${this.endpoint}/test`, formData);
  }
}
